import type { RequiredNonNull } from '@/types/required-non-null';
import { create, createAction } from '@/lib/store-utils';

export type CustomAlertConfig = {
  title?: string;
  message?: string;
  confirm?: string;
  cancel?: string;
};

type HookFormStoreState = {
  isOpen: boolean;
  isDirty: boolean;
  reset: (() => void) | null;
  resolve: ((value: boolean) => void) | null;
  checkElInForm: ((el: HTMLElement) => boolean) | null;
  dialogSource: 'route' | 'custom';
  customAlert: CustomAlertConfig | null;
};
export const INITIAL_STATE: HookFormStoreState = {
  isOpen: false,
  isDirty: false,
  reset: null,
  checkElInForm: null,
  resolve: null,
  dialogSource: 'route',
  customAlert: null,
};

const STORE_NAME = 'HookFormStore';

type HookFormActions = {
  setMethod: (
    params: Pick<
      RequiredNonNull<HookFormStoreState>,
      'checkElInForm' | 'reset'
    >,
  ) => void;
  setIsOpen: (source?: HookFormStoreState['dialogSource']) => Promise<boolean>;
  setCustomAlert: (value: CustomAlertConfig | null) => void;
  setIsDirty: (value: boolean) => void;
  confirm: () => void;
  cancel: () => void;
  resetState: () => void;
};

const ACTION_TYPES = {
  SET_METHOD: `${STORE_NAME}/setMethod`,
  SET_IS_OPEN: `${STORE_NAME}/setIsOpen`,
  SET_CUSTOM_ALERT: `${STORE_NAME}/setCustomAlert`,
  SET_IS_DIRTY: `${STORE_NAME}/setIsDirty`,
  CONFIRM: `${STORE_NAME}/confirm`,
  CANCEL: `${STORE_NAME}/cancel`,
  RESET_STATE: `${STORE_NAME}/resetState`,
};

type HookFormStore = HookFormStoreState & HookFormActions;

export const useHookFormStore = create<HookFormStore>(
  STORE_NAME,
  (set, get) => ({
    ...INITIAL_STATE,
    setMethod: ({ checkElInForm, reset }) => {
      set(
        { checkElInForm, reset },
        false,
        createAction(ACTION_TYPES.SET_METHOD, undefined),
      );
    },
    setIsOpen: (source) => {
      return new Promise<boolean>((resolve) => {
        set(
          {
            isOpen: true,
            resolve,
            dialogSource: source,
          },
          false,
          createAction(ACTION_TYPES.CONFIRM, undefined),
        );
      });
    },
    setCustomAlert: (customAlert) => {
      set(
        { customAlert },
        false,
        createAction(ACTION_TYPES.SET_CUSTOM_ALERT, customAlert),
      );
    },
    setIsDirty: (isDirty) => {
      set({ isDirty }, false, createAction(ACTION_TYPES.SET_IS_DIRTY, isDirty));
    },
    confirm: () => {
      get().reset?.();
      get().resolve?.(true);
      set(
        { isOpen: false, reset: null },
        false,
        createAction(ACTION_TYPES.CONFIRM, undefined),
      );
    },
    cancel: () => {
      get().resolve?.(false);
      set(
        { isOpen: false },
        false,
        createAction(ACTION_TYPES.CANCEL, undefined),
      );
    },
    resetState: () => {
      get().reset?.();
      set(
        INITIAL_STATE,
        false,
        createAction(ACTION_TYPES.RESET_STATE, undefined),
      );
    },
  }),
);
